import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiAward, FiSearch, FiX, FiAlertTriangle, FiAlertOctagon, FiClock, FiZap, FiDownload,
  FiPlus, FiSettings, FiTrash2, FiRotateCcw, FiFileText,
} from 'react-icons/fi';
import {
  listarLicenciamento, salvarRegistro, removerRegistro, baixarDocumentoRegistro,
  listarTiposRegistro, criarTipoRegistro, atualizarTipoRegistro,
  listarDocumentosRegistro, enviarDocumentoRegistro, excluirDocumentoRegistro, baixarDocumentoAvulso,
  ESFERAS, SITUACOES, estadoRegistro, aguardandoRevisao,
} from '../../../services/licenciamento';
import { atualizarCliente } from '../../../services/clientes';
import { TIPOS_ATIVIDADE } from '../DadosReceita';
import { formatarData, itensDoAlerta, ModalAlerta } from '../procuracoes/comum';
import kpi from '../procuracoes/Procuracoes.module.css';
import css from './Licenciamento.module.css';
import useEsc from '../../../hooks/useEsc';

/* Licenciamento: os registros que cada empresa precisa ter em dia. Não é
   o passo a passo de como tirar (isso é o módulo Processos) -- aqui é o
   retrato: tem ou não tem, número, validade e o documento.
   Colunas fixas = base comum (todos têm; Estadual só comércio/indústria);
   última coluna = específicos do cliente + sugestões pelo CNAE. */

const CHAVE_ALERTAS = 'licenciamento.alertasDispensados';
const PENDENTE = new Set(['nao_verificado', 'pendente', 'irregular']);

function normalizar(t) {
  return (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
const rotuloAtividade = (v) => TIPOS_ATIVIDADE.find(t => t.v === v)?.l || '—';

function TextoEstado({ r, tipo }) {
  const e = estadoRegistro(r);
  if (e === 'na') return <span className={css.na}>não se aplica</span>;
  const rot = {
    nao_verificado: '—', pendente: 'Pendente', irregular: 'Irregular', dispensado: 'Dispensado',
    regular: tipo?.tem_validade && r.validade ? `até ${formatarData(r.validade)}` : 'Regular',
    vencido: `venceu ${formatarData(r.validade)}`, vencendo: `vence ${formatarData(r.validade)}`,
  }[e];
  return <span className={`${css.pilula} ${css['e_' + e]}`}>{rot}</span>;
}

/* ════════════ Documentos do registro (vários, com histórico) ════════════
   Nada é apagado quando chega um novo. Na Constituição, marcar como
   "alteração contratual" reinicia o licenciamento do cliente: todos os
   outros registros voltam pra pendente, mantendo o que já foi lançado. */
function DocumentosRegistro({ linha, tipo, onMudou }) {
  const [docs, setDocs] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [f, setF] = useState({ descricao: '', data: '', alteracao: false });
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');
  const ehConstituicao = tipo.esfera === 'constituicao';

  const carregar = useCallback(() => listarDocumentosRegistro(linha.clienteId, tipo.id)
    .then(r => setDocs(r.items || [])).catch(e => setErro(e.message)), [linha.clienteId, tipo.id]);
  useEffect(() => { carregar(); }, [carregar]);

  async function enviar() {
    if (f.alteracao && !window.confirm(
      `Alteração contratual reinicia o licenciamento de ${linha.nome}: todos os outros registros voltam para "Pendente" `
      + '(nada do que já foi lançado é apagado). Continuar?')) return;
    setEnviando(true); setErro(''); setAviso('');
    try {
      const r = await enviarDocumentoRegistro(linha.clienteId, tipo.id, f, arquivo);
      setF({ descricao: '', data: '', alteracao: false }); setArquivo(null); setAberto(false);
      if (r.reiniciados) setAviso(`Licenciamento reiniciado: ${r.reiniciados} registro(s) voltaram para pendente.`);
      await carregar(); onMudou();
    } catch (e) { setErro(e.message); }
    finally { setEnviando(false); }
  }

  async function excluir(d) {
    if (!window.confirm(`Excluir o documento "${d.descricao}"?`)) return;
    try { await excluirDocumentoRegistro(d.id); await carregar(); onMudou(); }
    catch (e) { setErro(e.message); }
  }

  return (
    <div className={css.campo}>
      <span>{ehConstituicao ? 'Contrato social e alterações' : 'Outros documentos'} ({docs.length})</span>
      {docs.map(d => (
        <div key={d.id} className={css.docItem}>
          <button type="button" className={css.docLink} onClick={() => baixarDocumentoAvulso(d.id, d.arquivo_nome).catch(e => setErro(e.message))}>
            <FiFileText size={13} /> <strong>{d.descricao}</strong>
            {d.data && <small>{formatarData(d.data)}</small>}
            {d.alteracao && <em className={css.tagAlteracao}>alteração</em>}
          </button>
          <button type="button" className={css.btnIcone} title="Excluir documento" onClick={() => excluir(d)}><FiTrash2 size={12} /></button>
        </div>
      ))}
      {aberto ? (
        <div className={css.docForm}>
          <input placeholder={ehConstituicao ? 'Ex.: 3ª alteração contratual' : 'Descrição do documento'} value={f.descricao}
            onChange={e => setF({ ...f, descricao: e.target.value })} autoFocus />
          <input type="date" value={f.data} onChange={e => setF({ ...f, data: e.target.value })} title="Data do documento" />
          <input type="file" onChange={e => setArquivo(e.target.files?.[0] || null)} />
          {ehConstituicao && (
            <label className={css.check}>
              <input type="checkbox" checked={f.alteracao} onChange={e => setF({ ...f, alteracao: e.target.checked })} />
              É alteração contratual (reinicia o licenciamento do cliente)
            </label>
          )}
          <div className={css.acoesForm}>
            <button type="button" className={css.btnSecundario} onClick={() => setAberto(false)}>Cancelar</button>
            <button type="button" className={css.btnPrimario} disabled={enviando || !f.descricao.trim() || !arquivo} onClick={enviar}>
              {enviando ? 'Enviando...' : 'Adicionar'}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className={css.btnSecundario} style={{ alignSelf: 'flex-start' }} onClick={() => setAberto(true)}>
          <FiPlus size={13} /> {ehConstituicao ? 'Adicionar contrato / alteração' : 'Adicionar documento'}
        </button>
      )}
      {aviso && <p className={css.avisoReinicio}><FiRotateCcw size={12} /> {aviso}</p>}
      {erro && <p className={css.erro}>{erro}</p>}
    </div>
  );
}

/* ════════════ Registro de um cliente ════════════ */
function DrawerRegistro({ linha, tipo, registro, onFechar, onSalvo, onMudou }) {
  useEsc(onFechar);
  const [f, setF] = useState({
    situacao: registro?.virtual ? 'nao_verificado' : (registro?.situacao || 'pendente'),
    opcao: registro?.opcao || '', numero: registro?.numero || '',
    emissao: registro?.emissao || '', validade: registro?.validade || '', observacao: registro?.observacao || '',
  });
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const set = (c, v) => setF(x => ({ ...x, [c]: v }));
  const salvo = registro && !registro.virtual;

  async function salvar() {
    setSalvando(true); setErro('');
    try { await salvarRegistro(linha.clienteId, tipo.id, f, arquivo); onSalvo(); }
    catch (e) { setErro(e.message); setSalvando(false); }
  }
  async function remover() {
    const msg = registro?.base
      ? `Limpar o registro "${tipo.nome}" de ${linha.nome}? Ele volta pra "não verificado".`
      : `Tirar "${tipo.nome}" de ${linha.nome}? O documento anexado também é apagado.`;
    if (!window.confirm(msg)) return;
    try { await removerRegistro(linha.clienteId, tipo.id); onSalvo(); }
    catch (e) { setErro(e.message); }
  }

  return (
    <div className={css.fundo}>
      <div className={css.drawer} onClick={e => e.stopPropagation()}>
        <div className={css.drawerTopo}>
          <div>
            <small>{linha.codigo} — {linha.nome}</small>
            <h3>{tipo.nome}</h3>
            {tipo.descricao && <p>{tipo.descricao}</p>}
          </div>
          <button type="button" className={css.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.drawerCorpo}>
          <label className={css.campo}>
            <span>Situação</span>
            <select value={f.situacao} onChange={e => set('situacao', e.target.value)}>
              {Object.entries(SITUACOES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          {f.situacao === 'regular' && (
            <p className={css.dica}>Regular = concluído: exige o documento anexado{tipo.tem_validade ? ' e a validade' : ''}.</p>
          )}
          {tipo.opcoes?.length > 0 && (
            <label className={css.campo}>
              <span>Onde é registrado</span>
              <select value={f.opcao} onChange={e => set('opcao', e.target.value)}>
                <option value="">Selecione...</option>
                {tipo.opcoes.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
          )}
          <label className={css.campo}>
            <span>Número / inscrição</span>
            <input value={f.numero} onChange={e => set('numero', e.target.value)} />
          </label>
          <div className={css.linha2}>
            <label className={css.campo}>
              <span>Emissão</span>
              <input type="date" value={f.emissao} onChange={e => set('emissao', e.target.value)} />
            </label>
            {tipo.tem_validade && (
              <label className={css.campo}>
                <span>Validade *</span>
                <input type="date" value={f.validade} onChange={e => set('validade', e.target.value)} />
              </label>
            )}
          </div>
          <div className={css.campo}>
            <span>Documento principal</span>
            {registro?.tem_arquivo && !arquivo && (
              <button type="button" className={css.docAtual}
                onClick={() => baixarDocumentoRegistro(linha.clienteId, tipo.id, registro.arquivo_nome).catch(e => setErro(e.message))}>
                <FiDownload size={13} /> {registro.arquivo_nome}
              </button>
            )}
            <input type="file" onChange={e => setArquivo(e.target.files?.[0] || null)} />
            {registro?.tem_arquivo && <small className={css.dica}>Escolha um arquivo só se for trocar o documento.</small>}
          </div>
          <DocumentosRegistro linha={linha} tipo={tipo} onMudou={onMudou} />
          <label className={css.campo}>
            <span>Observação</span>
            <textarea rows={2} value={f.observacao} onChange={e => set('observacao', e.target.value)} />
          </label>
          {aguardandoRevisao(registro) && (
            <p className={css.avisoReinicio}><FiRotateCcw size={12} /> Voltou para pendente em {new Date(registro.reiniciado_em).toLocaleDateString('pt-BR')}
              {' '}({registro.reiniciado_motivo}). Antes estava: {SITUACOES[registro.situacao_anterior] || '—'}.</p>
          )}
          {salvo && registro.verificado_em && (
            <p className={css.dica}>Última conferência: {new Date(registro.verificado_em).toLocaleString('pt-BR')}{registro.atualizado_por ? ` por ${registro.atualizado_por}` : ''}</p>
          )}
          {erro && <p className={css.erro}>{erro}</p>}
        </div>
        <div className={css.drawerRodape}>
          {salvo && <button type="button" className={css.btnPerigo} onClick={remover}><FiTrash2 size={13} /> {registro.base ? 'Limpar' : 'Tirar do cliente'}</button>}
          <span style={{ flex: 1 }} />
          <button type="button" className={css.btnSecundario} onClick={onFechar}>Cancelar</button>
          <button type="button" className={css.btnPrimario} onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
}

/* ════════════ Ficha do cliente: todos os registros + incluir específico ════════════ */
function DrawerCliente({ linha, tipos, onFechar, onAbrir, onMudou }) {
  useEsc(onFechar);
  const [novo, setNovo] = useState('');
  const [atividade, setAtividade] = useState(linha.tipoAtividade ?? '');
  const [erro, setErro] = useState('');
  const tipoPorId = new Map(tipos.map(t => [t.id, t]));
  const jaTem = new Set(linha.registros.map(r => r.tipoId));
  const disponiveis = tipos.filter(t => !jaTem.has(t.id));

  async function salvarAtividade(v) {
    setAtividade(v); setErro('');
    try { await atualizarCliente(linha.clienteId, { tipo_atividade: v === '' ? null : Number(v) }); onMudou(); }
    catch (e) { setErro(e.message); }
  }

  const porEsfera = linha.registros.reduce((m, r) => {
    const t = tipoPorId.get(r.tipoId); if (!t) return m;
    (m[t.esfera] = m[t.esfera] || []).push({ r, t }); return m;
  }, {});

  return (
    <div className={css.fundo}>
      <div className={css.drawer} onClick={e => e.stopPropagation()}>
        <div className={css.drawerTopo}>
          <div><small>{linha.codigo}</small><h3>{linha.nome}</h3></div>
          <button type="button" className={css.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.drawerCorpo}>
          <label className={css.campo}>
            <span>Tipo de atividade</span>
            <select value={atividade} onChange={e => salvarAtividade(e.target.value)}>
              <option value="">Pelo CNAE (automático){linha.atividadeDeduzida ? ` — hoje: ${rotuloAtividade(linha.atividade)}` : ''}</option>
              {TIPOS_ATIVIDADE.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <p className={css.dica}>Inscrição Estadual entra na base só de quem tem comércio ou indústria.</p>
          {linha.aRevisar > 0 && (
            <p className={css.avisoReinicio}><FiRotateCcw size={12} /> Licenciamento reiniciado por alteração contratual em
              {' '}{new Date(linha.reiniciadoEm).toLocaleDateString('pt-BR')}: {linha.aRevisar} registro(s) ainda a revisar.</p>
          )}
          {erro && <p className={css.erro}>{erro}</p>}

          {Object.keys(ESFERAS).filter(e => porEsfera[e]).map(e => (
            <div key={e} className={css.bloco}>
              <h4>{ESFERAS[e]}</h4>
              {porEsfera[e].map(({ r, t }) => (
                <button key={t.id} type="button" className={css.itemFicha} onClick={() => onAbrir(linha, t, r)}>
                  <span><strong>{t.nome}</strong>{r.opcao ? ` · ${r.opcao}` : ''}{r.numero ? ` · nº ${r.numero}` : ''}</span>
                  <TextoEstado r={r} tipo={t} />
                </button>
              ))}
            </div>
          ))}

          {linha.sugestoes.length > 0 && (
            <div className={css.bloco}>
              <h4><FiZap size={11} /> Sugeridos pelo CNAE</h4>
              {linha.sugestoes.map(id => tipoPorId.get(id)).filter(Boolean).map(t => (
                <button key={t.id} type="button" className={`${css.itemFicha} ${css.itemSugerido}`} onClick={() => onAbrir(linha, t, null)}>
                  <span><strong>{t.nome}</strong> · {t.descricao}</span>
                  <span className={css.na}>incluir ou dispensar</span>
                </button>
              ))}
            </div>
          )}

          <div className={css.incluir}>
            <select value={novo} onChange={e => setNovo(e.target.value)}>
              <option value="">Incluir outro registro...</option>
              {Object.entries(ESFERAS).map(([e, rot]) => {
                const op = disponiveis.filter(t => t.esfera === e);
                return op.length ? <optgroup key={e} label={rot}>{op.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}</optgroup> : null;
              })}
            </select>
            <button type="button" className={css.btnSecundario} disabled={!novo}
              onClick={() => { onAbrir(linha, tipoPorId.get(novo), null); setNovo(''); }}>
              <FiPlus size={13} /> Incluir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════ Catálogo de tipos ════════════ */
function DrawerTipos({ onFechar, onMudou }) {
  useEsc(onFechar);
  const [tipos, setTipos] = useState([]);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState('');
  const carregar = useCallback(() => listarTiposRegistro().then(r => setTipos(r.items || [])).catch(e => setErro(e.message)), []);
  useEffect(() => { carregar(); }, [carregar]);

  async function salvar() {
    setErro('');
    const d = { ...editando, opcoes: editando.opcoesTexto, cnaes: editando.cnaesTexto };
    delete d.opcoesTexto; delete d.cnaesTexto;
    try {
      if (editando.id) await atualizarTipoRegistro(editando.id, d); else await criarTipoRegistro(d);
      setEditando(null); carregar(); onMudou();
    } catch (e) { setErro(e.message); }
  }
  const abrir = (t) => setEditando({
    id: t.id, nome: t.nome || '', esfera: t.esfera || 'especial', base: t.base || '', tem_validade: !!t.tem_validade,
    descricao: t.descricao || '', ativo: t.ativo ?? true,
    opcoesTexto: (t.opcoes || []).join(', '), cnaesTexto: (t.cnaes || []).join(', '),
  });

  return (
    <div className={css.fundo}>
      <div className={`${css.drawer} ${css.drawerLargo}`} onClick={e => e.stopPropagation()}>
        <div className={css.drawerTopo}>
          <div><h3>Tipos de registro</h3><p>Lista aberta: edite, desative ou crie os que precisar.</p></div>
          <button type="button" className={css.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>
        <div className={css.drawerCorpo}>
          {erro && <p className={css.erro}>{erro}</p>}
          {editando ? (
            <div className={css.formTipo}>
              <div className={css.linha2}>
                <label className={css.campo}><span>Nome *</span><input value={editando.nome} onChange={e => setEditando({ ...editando, nome: e.target.value })} autoFocus /></label>
                <label className={css.campo}><span>Esfera</span>
                  <select value={editando.esfera} onChange={e => setEditando({ ...editando, esfera: e.target.value })}>
                    {Object.entries(ESFERAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </label>
              </div>
              <label className={css.campo}><span>Descrição</span><input value={editando.descricao} onChange={e => setEditando({ ...editando, descricao: e.target.value })} /></label>
              <label className={css.campo}><span>Quem tem</span>
                <select value={editando.base} onChange={e => setEditando({ ...editando, base: e.target.value })}>
                  <option value="todos">Todos os clientes (base comum)</option>
                  <option value="comercio_industria">Só comércio e indústria</option>
                  <option value="">Só quem tem (incluído no cliente)</option>
                </select>
              </label>
              <label className={css.campo}><span>Opções de onde é registrado (separadas por vírgula)</span>
                <input value={editando.opcoesTexto} onChange={e => setEditando({ ...editando, opcoesTexto: e.target.value })} placeholder="ex.: JUCESP, Cartório (RCPJ), OAB" />
              </label>
              {!editando.base && (
                <label className={css.campo}><span>CNAEs que sugerem (início do código, separados por vírgula)</span>
                  <input value={editando.cnaesTexto} onChange={e => setEditando({ ...editando, cnaesTexto: e.target.value })} placeholder="ex.: 8630, 8610" />
                </label>
              )}
              <label className={css.check}><input type="checkbox" checked={editando.tem_validade} onChange={e => setEditando({ ...editando, tem_validade: e.target.checked })} /> Tem validade (precisa renovar)</label>
              <label className={css.check}><input type="checkbox" checked={editando.ativo} onChange={e => setEditando({ ...editando, ativo: e.target.checked })} /> Ativo</label>
              <div className={css.acoesForm}>
                <button type="button" className={css.btnSecundario} onClick={() => setEditando(null)}>Voltar</button>
                <button type="button" className={css.btnPrimario} onClick={salvar} disabled={!editando.nome.trim()}>Salvar</button>
              </div>
            </div>
          ) : (
            <>
              <button type="button" className={css.btnPrimario} style={{ marginBottom: 12 }}
                onClick={() => abrir({ esfera: 'especial', base: '' })}><FiPlus size={13} /> Novo tipo</button>
              {Object.entries(ESFERAS).map(([e, rot]) => {
                const doGrupo = tipos.filter(t => t.esfera === e);
                if (!doGrupo.length) return null;
                return (
                  <div key={e} className={css.bloco}>
                    <h4>{rot}</h4>
                    {doGrupo.map(t => (
                      <button key={t.id} type="button" className={`${css.itemFicha} ${!t.ativo ? css.inativo : ''}`} onClick={() => abrir(t)}>
                        <span>
                          <strong>{t.nome}</strong>
                          {t.base === 'todos' && <em className={css.tag}>base</em>}
                          {t.base === 'comercio_industria' && <em className={css.tag}>comércio/indústria</em>}
                          {t.tem_validade && <em className={css.tagValidade}>validade</em>}
                          {!t.ativo && <em className={css.tag}>inativo</em>}
                        </span>
                        <small>{t.cnaes?.length ? `CNAE ${t.cnaes.slice(0, 5).join(', ')}${t.cnaes.length > 5 ? '…' : ''}` : t.descricao}</small>
                      </button>
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ════════════ Tela ════════════ */
export default function Licenciamento() {
  const [dados, setDados] = useState({ tipos: [], items: [] });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState(null);
  const [registroAberto, setRegistroAberto] = useState(null); // { linha, tipo, registro }
  const [clienteAberto, setClienteAberto] = useState(null);   // clienteId
  const [tiposAberto, setTiposAberto] = useState(false);
  const [alertaFechado, setAlertaFechado] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    return listarLicenciamento().then(setDados).catch(e => setErro(e.message)).finally(() => setCarregando(false));
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const tipoPorId = useMemo(() => new Map(dados.tipos.map(t => [t.id, t])), [dados.tipos]);
  /* colunas fixas: a base comum, na ordem do catálogo */
  const colunasBase = dados.tipos.filter(t => t.base);

  const linhas = useMemo(() => dados.items.map(l => {
    const estados = l.registros.map(estadoRegistro);
    return {
      ...l,
      pendencias: estados.filter(e => PENDENTE.has(e)).length,
      vencidos: estados.filter(e => e === 'vencido').length,
      vencendo: estados.filter(e => e === 'vencendo').length,
      aRevisar: l.registros.filter(aguardandoRevisao).length,
    };
  }), [dados.items]);

  const FILTROS = {
    pendencia: l => l.pendencias > 0, vencido: l => l.vencidos > 0,
    vencendo: l => l.vencendo > 0, sugestao: l => l.sugestoes.length > 0,
  };
  const CARDS = [
    { id: 'pendencia', titulo: 'Com pendência', sub: 'Não verificado, pendente ou irregular', icone: FiAlertTriangle, cor: kpi.kpiLaranja },
    { id: 'vencido', titulo: 'Vencidos', sub: 'Precisa renovar', icone: FiAlertOctagon, cor: kpi.kpiVermelho },
    { id: 'vencendo', titulo: 'Vencendo', sub: 'Próximos 30 dias', icone: FiClock, cor: kpi.kpiRoxo },
    { id: 'sugestao', titulo: 'Sugestões', sub: 'Registros sugeridos pelo CNAE', icone: FiZap, cor: kpi.kpiAzul },
  ];

  const q = normalizar(busca.trim());
  const visiveis = linhas.filter(l =>
    (!filtro || FILTROS[filtro](l)) && (!q || normalizar(`${l.codigo} ${l.nome}`).includes(q)));

  /* alerta ao abrir: registros com validade vencendo em até 15 dias */
  const alertas = useMemo(() => itensDoAlerta(
    linhas.flatMap(l => l.registros.filter(r => r.situacao === 'regular' && r.validade).map(r => ({
      id: r.id, vencimento: r.validade, diasRestantes: r.dias_restantes,
      codigo: l.codigo, clienteNome: l.nome, descricao: tipoPorId.get(r.tipoId)?.nome || '',
    }))), CHAVE_ALERTAS), [linhas, tipoPorId]);

  const linhaAberta = clienteAberto ? linhas.find(l => l.clienteId === clienteAberto) : null;
  const abrirRegistro = (linha, tipo, registro) => setRegistroAberto({ linha, tipo, registro });

  return (
    <div className={css.page}>
      <div className={css.header}>
        <div className={css.headerIcone}><FiAward size={20} /></div>
        <h1 className={css.titulo}>Licenciamento</h1>
        <div className={css.buscaWrap}>
          <FiSearch size={14} />
          <input placeholder="Buscar cliente..." value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        <button type="button" className={css.btnSecundario} onClick={() => setTiposAberto(true)}>
          <FiSettings size={13} /> Tipos de registro
        </button>
      </div>

      <div className={kpi.kpis}>
        {CARDS.map(c => {
          const Icone = c.icone; const ativo = filtro === c.id;
          return (
            <button key={c.id} type="button" className={`${kpi.kpi} ${ativo ? kpi.kpiAtivo : ''}`} onClick={() => setFiltro(ativo ? null : c.id)}>
              <span className={`${kpi.kpiIcone} ${c.cor}`}><Icone size={17} /></span>
              <span className={kpi.kpiTexto}>
                <strong className={kpi.kpiValor}>{linhas.filter(FILTROS[c.id]).length}</strong>
                <span className={kpi.kpiTitulo}>{c.titulo}</span>
                <span className={kpi.kpiSub}>{c.sub}</span>
              </span>
            </button>
          );
        })}
      </div>

      {erro && <p className={css.erro}>{erro}</p>}

      <div className={css.conteudo}>
        <div className={css.contador}>{visiveis.length} {visiveis.length === 1 ? 'cliente' : 'clientes'}</div>
        <div className={css.tabelaWrap}>
          <table className={css.tabela}>
            <thead>
              <tr>
                <th className={css.thCliente}>Cliente</th>
                {colunasBase.map(t => <th key={t.id} className={css.thBase} title={t.descricao || ''}>{t.nome}</th>)}
                <th>Específicos</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={colunasBase.length + 2} className={css.vazio}>Carregando...</td></tr>
              ) : visiveis.length === 0 ? (
                <tr><td colSpan={colunasBase.length + 2} className={css.vazio}>Nenhum cliente nessa condição.</td></tr>
              ) : visiveis.map(l => {
                const porTipo = new Map(l.registros.map(r => [r.tipoId, r]));
                const especificos = l.registros.filter(r => !tipoPorId.get(r.tipoId)?.base);
                return (
                  <tr key={l.clienteId}>
                    <td>
                      <button type="button" className={css.cliente} onClick={() => setClienteAberto(l.clienteId)}>
                        <span className={css.codigo}>{l.codigo}</span> {l.nome}
                        {l.pendente && <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: 5, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.03em' }} title="Cliente vindo do onboarding: sai quando nenhum registro estiver como não verificado">Pendente</span>}
                      </button>
                      <div className={css.atividade}>
                        {rotuloAtividade(l.atividade)}{l.atividadeDeduzida ? ' (pelo CNAE)' : ''}
                      </div>
                      {l.aRevisar > 0 && (
                        <div className={css.seloReinicio} title="Alteração contratual: o licenciamento foi reiniciado">
                          <FiRotateCcw size={10} /> reiniciado em {new Date(l.reiniciadoEm).toLocaleDateString('pt-BR')} · {l.aRevisar} a revisar
                        </div>
                      )}
                    </td>
                    {colunasBase.map(t => {
                      const r = porTipo.get(t.id);
                      return (
                        <td key={t.id} className={css.celBase}>
                          {r ? (
                            <button type="button" className={css.celBtn} onClick={() => abrirRegistro(l, t, r)}
                              title={[r.opcao, r.numero && `nº ${r.numero}`, SITUACOES[r.situacao]].filter(Boolean).join(' · ')}>
                              <TextoEstado r={r} tipo={t} />
                            </button>
                          ) : <TextoEstado r={null} />}
                        </td>
                      );
                    })}
                    <td>
                      <div className={css.chips}>
                        {especificos.map(r => {
                          const t = tipoPorId.get(r.tipoId); if (!t) return null;
                          const e = estadoRegistro(r);
                          return (
                            <button key={r.tipoId} type="button" className={`${css.chip} ${css['c_' + e]}`} onClick={() => abrirRegistro(l, t, r)}
                              title={SITUACOES[r.situacao]}>
                              {t.nome}{r.validade && e !== 'dispensado' ? ` · ${formatarData(r.validade).slice(3)}` : ''}
                            </button>
                          );
                        })}
                        {l.sugestoes.map(id => tipoPorId.get(id)).filter(Boolean).map(t => (
                          <button key={t.id} type="button" className={`${css.chip} ${css.chipSugerido}`} onClick={() => abrirRegistro(l, t, null)}
                            title="Sugerido pelo CNAE -- clique pra incluir ou marcar como dispensado">
                            <FiZap size={10} /> {t.nome}
                          </button>
                        ))}
                        <button type="button" className={css.chipMais} title="Incluir registro" onClick={() => setClienteAberto(l.clienteId)}>
                          <FiPlus size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {linhaAberta && !registroAberto && (
        <DrawerCliente linha={linhaAberta} tipos={dados.tipos} onFechar={() => setClienteAberto(null)}
          onAbrir={abrirRegistro} onMudou={carregar} />
      )}
      {registroAberto && (
        <DrawerRegistro {...registroAberto} onMudou={carregar} onFechar={() => setRegistroAberto(null)}
          onSalvo={() => { setRegistroAberto(null); carregar(); }} />
      )}
      {tiposAberto && <DrawerTipos onFechar={() => setTiposAberto(false)} onMudou={carregar} />}
      {!alertaFechado && alertas.length > 0 && (
        <ModalAlerta itens={alertas} titulo="Registros vencendo" singular="registro" plural="registros"
          masculino chave={CHAVE_ALERTAS} onFechar={() => setAlertaFechado(true)} />
      )}
    </div>
  );
}
